import React, { useState, useEffect } from 'react';
import { reports } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const Reports = () => {
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadReports();
  }, [dateRange]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const [sumRes, revRes, custRes, empRes] = await Promise.all([
        reports.summary(params),
        reports.revenue(params),
        reports.customers(params),
        reports.employees(params)
      ]);

      setSummary(sumRes.data);
      setRevenue(revRes.data);
      setCustomers(custRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return <div>Loading reports...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Reports</h1>
      </div>

      <div className="form-row" style={{ marginBottom: 24 }}>
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            className="form-input"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-input"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Entries</div>
          <div className="stat-value">{summary?.totalEntries || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unbilled</div>
          <div className="stat-value warning">{formatCurrency(summary?.unbilled?.amount)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Invoiced</div>
          <div className="stat-value">{formatCurrency(summary?.invoiced?.amount)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Paid</div>
          <div className="stat-value success">{formatCurrency(summary?.paid?.amount)}</div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 className="card-title">Revenue Over Time</h3>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Top Customers */}
        <div className="card">
          <h3 className="card-title">Top Customers</h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Hours</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {customers.slice(0, 10).map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.totalHours.toFixed(1)}</td>
                    <td>{formatCurrency(c.totalRevenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Employee Hours */}
        <div className="card">
          <h3 className="card-title">Employee Hours</h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Hours</th>
                  <th>Entries</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(e => (
                  <tr key={e.id}>
                    <td>{e.name}</td>
                    <td>{e.totalHours.toFixed(1)}</td>
                    <td>{e.entryCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
